/**
 * AdminPageHeader — the shared top bar for the admin SPA list pages.
 *
 * Replaces the per-route header blocks that had drifted (each carried its own
 * copy of the nav links + Sign Out, with inconsistent hover styling). One
 * component now owns DESIGN.md compliance: no hover visual change and the
 * canonical H1.
 *
 * Layout: visible H1 + optional subtitle on the left, page actions anchored
 * top-right (see HeaderActions — inline from `sm`, kebab below it). Global
 * destinations and Logout are NOT here: they live in the persistent `AppNav`
 * bar above every page.
 */
import type { ReactNode } from "react";
import { HeaderActions, type HeaderAction } from "./HeaderActions";
import styles from "./AdminPageHeader.module.css";

/**
 * A page link: in-app by route, or out of the app by URL — the job
 * page's way out to the job in the Company's rental system (#119),
 * opened in a new tab so the thread stays where it was.
 */
type NavLink = { to: string; label: string } | { href: string; label: string };

interface AdminPageHeaderProps {
  /** The page H1. Every admin page must have exactly one. */
  title: string;
  /** Secondary line under the title. */
  subtitle?: ReactNode;
  /**
   * Page-specific links (e.g. Back to a parent list). Global destinations
   * belong in `AppNav`, never here.
   */
  links?: NavLink[];
}

export function AdminPageHeader({
  title,
  subtitle,
  links = [],
}: AdminPageHeaderProps) {
  const actions: HeaderAction[] = links.map((link) =>
    "to" in link
      ? { kind: "link", to: link.to, label: link.label }
      : {
          kind: "external-link",
          href: link.href,
          target: "_blank",
          rel: "noreferrer",
          label: link.label,
        },
  );

  return (
    <div className={styles.header}>
      <div className={styles.titleBlock}>
        <h1 className={styles.title}>{title}</h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      <HeaderActions actions={actions} />
    </div>
  );
}
